/**
 * Seitenübersicht: jede öffentliche Seite mit einem festen internen Link.
 * Daten und Begründung: src/lib/site-overview.functions.ts. Themenseiten
 * (/themen/) stehen hier bewusst nicht.
 */
import { createFileRoute } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import { filterNavItems, navItems } from "@/components/nav-data";
import { getSiteOverview, type SiteOverviewLink } from "@/lib/site-overview.functions";

const TITLE = "Seitenübersicht | creategy";
const DESCRIPTION =
  "Alle Seiten von creategy auf einen Blick: Leistungen, Projekte, Blogbeiträge und kostenlose Unterlagen.";

export const Route = createFileRoute("/seitenuebersicht")({
  loader: () => getSiteOverview(),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: "https://creategy.de/seitenuebersicht" }],
  }),
  component: SiteOverviewPage,
});

function LinkList({ title, links }: { title: string; links: SiteOverviewLink[] }) {
  if (links.length === 0) return null;
  return (
    <section className="mt-12">
      <h2 className="heading-h3">{title}</h2>
      <ul className="mt-4 grid gap-x-8 gap-y-2 font-body text-base sm:grid-cols-2 lg:grid-cols-3">
        {links.map((link) => (
          <li key={link.to}>
            <a href={link.to} className="text-primary underline-offset-4 hover:underline">
              {link.label}
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}

function SiteOverviewPage() {
  const { blog, cases, downloads, enabledFlags } = Route.useLoaderData();

  const pages: SiteOverviewLink[] = [
    { to: "/", label: "Startseite" },
    { to: "/wahlkampfagentur", label: "Wahlkampfagentur" },
    { to: "/buergermeisterwahl-projekte", label: "Projekte Bürgermeisterwahl" },
    { to: "/kontakt", label: "Kontakt" },
  ];
  for (const item of filterNavItems(navItems, enabledFlags)) {
    if (item.type === "link") pages.push({ to: item.to, label: item.label });
    else
      for (const child of item.children)
        if (!child.external) pages.push({ to: child.to, label: child.label });
  }

  return (
    <Layout>
      <section>
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <header className="max-w-3xl">
            <h1 className="heading-h1">Seitenübersicht</h1>
            <p className="mt-5 font-body text-lg leading-relaxed text-muted-foreground">
              Alle Seiten von creategy auf einen Blick.
            </p>
          </header>
          <LinkList title="Leistungen und Agentur" links={pages} />
          <LinkList title="Projekte" links={cases} />
          <LinkList title="Kostenlose Unterlagen" links={downloads} />
          <LinkList title="Blog" links={blog} />
        </div>
      </section>
    </Layout>
  );
}
